import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Mail, Lock, Shield, User, Phone } from 'lucide-react';
import api from '../utils/api';

// Support fix 2026-09-24: return to the shared post after login (set by PublicAtcPost)
const consumeAfterLoginRedirect = () => {
  try {
    const r = sessionStorage.getItem('after_login_redirect');
    sessionStorage.removeItem('after_login_redirect');
    if (r && r.startsWith('/') && !r.startsWith('//')) return r;
  } catch (_) {}
  return '/dashboard';
};

const Login = () => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [usePin, setUsePin] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (requires2FA) {
        // Login with 2FA
        const response = await api.post('/auth/login-2fa', {
          login_id: loginId,
          password: usePin ? undefined : password,
          pin: usePin ? pin : undefined,
          code
        });
        
        if (response.data.token) {
          login(response.data.token, response.data.user);
          toast.success('Login successful!');
          navigate(consumeAfterLoginRedirect());
        }
      } else {
        // Validate before submitting
        if (!loginId.trim()) {
          toast.error('Please enter email, username, or mobile');
          setLoading(false);
          return;
        }
        
        if (!usePin && !password) {
          toast.error('Please enter your password');
          setLoading(false);
          return;
        }
        
        if (usePin && (!pin || pin.length !== 6)) {
          toast.error('Please enter a valid 6-digit PIN');
          setLoading(false);
          return;
        }
        
        // Regular login attempt - supports email/username/mobile
        const payload = {
          login_id: loginId.trim()
        };
        
        if (usePin && pin) {
          payload.pin = pin;
        } else if (password) {
          payload.password = password;
        }
        
        const response = await api.post('/auth/login', payload);
        
        if (response.data.requires_2fa) {
          // 2FA required, show 2FA input
          setRequires2FA(true);
          toast.info('Please enter your 2FA code from Google Authenticator');
        } else if (response.data.token) {
          login(response.data.token, response.data.user);
          toast.success('Login successful!');
          navigate(consumeAfterLoginRedirect());
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="glass-card p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="ANANT WEB PORTAL" className="h-16 w-auto" />
          </div>
          
          <h1 className="text-4xl font-bold mb-2 text-center" data-testid="login-title">Welcome Back</h1>
          <p className="text-muted-foreground text-center mb-8">
            Login to your account
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Login ID - Email/Username/Mobile */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Email / Username / Mobile
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  autoComplete="username"
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary transition-colors"
                  placeholder="Enter email, username or mobile"
                  required
                  data-testid="login-id-input"
                />
              </div>
            </div>

            {/* Password or PIN toggle */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setUsePin(false)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${!usePin ? 'bg-primary text-white' : 'bg-white/5'}`}
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => setUsePin(true)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${usePin ? 'bg-primary text-white' : 'bg-white/5'}`}
              >
                6-Digit PIN
              </button>
            </div>

            {/* Password */}
            {!usePin && (
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary transition-colors"
                    placeholder="Enter your password"
                    required={!usePin}
                    data-testid="password-input"
                  />
                </div>
              </div>
            )}

            {/* 6-Digit PIN */}
            {usePin && (
              <div>
                <label className="block text-sm font-medium mb-2">6-Digit PIN</label>
                <div className="relative">
                  <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="off"
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary transition-colors text-center text-2xl tracking-widest"
                    placeholder="• • • • • •"
                    maxLength={6}
                    required={usePin}
                    data-testid="pin-input"
                  />
                </div>
              </div>
            )}

            {/* 2FA Code */}
            {requires2FA && (
              <div>
                <label className="block text-sm font-medium mb-2">2FA Code</label>
                <div className="relative">
                  <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="one-time-code"
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary transition-colors text-center text-xl tracking-widest"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    required
                    data-testid="2fa-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Enter the code from Google Authenticator app
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : (requires2FA ? 'Verify & Login' : 'Login')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
